import React, { Suspense } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import PublicRoutes from "./PublicRoutes";
import AdminRoutes from "./AdminRoutes";
import SuperAdminRoutes from "./SuperAdminRoutes";
import ParentPortalRoutes from "./ParentPortalRoutes";
import NotFound from "@/pages/NotFound";
import Auth from "@/pages/Auth";
import AuthRouteHandler from "@/components/auth/AuthRouteHandler";
import Pricing from "@/pages/Pricing";
import PricingPlan from "@/pages/PricingPlan";
import SubscriptionCheckout from "@/pages/SubscriptionCheckout";
import Unauthorized from "@/pages/Unauthorized";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Features from "@/pages/Features";
import Index from "@/pages/Index";
import EmailVerification from "@/pages/EmailVerification";
import { AdminRoleGuard } from "@/components/layout/AdminRoleGuard";
import AdminLayout from "@/components/layout/AdminLayout";
import DashboardAI from "@/pages/DashboardAI";
import MaterialDashboard from "@/pages/MaterialDashboard";
import Profile from "@/pages/Profile";
import Messages from "@/pages/Messages";
import ParentPortalSettings from "@/pages/admin/ParentPortalSettings";
import AboutUs from "@/pages/AboutUs";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import TermsOfService from "@/pages/TermsOfService";
import Cookies from "@/pages/Cookies";
import Eula from "@/pages/Eula";
import DanceStudios from "@/pages/DanceStudios";
import SummerCamps from "@/pages/SummerCamps";
import SwimClasses from "@/pages/SwimClasses";
import AfterSchool from "@/pages/AfterSchool";
import EquestrianTraining from "@/pages/EquestrianTraining";
import MusicSchools from "@/pages/MusicSchools";
import KidsGyms from "@/pages/KidsGyms";
import PlayCenters from "@/pages/PlayCenters";
import BusinessTrial from "@/pages/BusinessTrial";
import BusinessTrialSignup from "@/pages/business-trial/BusinessTrialSignup";
import AdvancedSignup from "@/pages/AdvancedSignup";
import SignupCancelled from "@/pages/SignupCancelled";
import Feedback from "@/pages/Feedback";
import ResetPassword from "@/pages/ResetPassword";
import SetPassword from "@/pages/SetPassword";
import StaffProfile from "@/pages/StaffProfile";
import AccountSuspended from '@/pages/AccountSuspended';
import TestInviteFunction from '@/pages/TestInviteFunction';
import MobileAuthTest from '@/pages/MobileAuthTest';
import NewClassPage from '@/pages/classes/NewClassPage';
import NewEventPage from '@/pages/classes/NewEventPage';
import EditClassPage from '@/pages/classes/EditClassPage';
import { AuthGate } from "@/components/layout/AuthGate";
import { SubscriptionGuard } from "@/components/auth/SubscriptionGuard";
import CheckIn from '@/pages/CheckIn';
import Apparel from "@/pages/Apparel";
import PublicWhatsNew from '@/pages/PublicWhatsNew';
import SignupFlow from '@/pages/SignupFlow';
import SearchDemo from '@/pages/SearchDemo';
import CheckoutSuccess from '@/pages/CheckoutSuccess';
import OurWhy from '@/pages/OurWhy';
import HiveShareLanding from '@/pages/HiveShareLanding';
import HiveShareCreator from '@/pages/HiveShareCreator';
import HiveShareLive from '@/pages/HiveShareLive';
import HiveShareFAQ from '@/pages/HiveShareFAQ';

// Loading component for suspended routes
const LazyLoadingSpinner = () => (
  <div className="flex justify-center items-center h-full py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes that don't require authentication */}
      <Route index element={<Index />} />
      <Route path="/dance-studios" element={<DanceStudios />} />
      <Route path="/summer-camps" element={<SummerCamps />} />
      <Route path="/swim-classes" element={<SwimClasses />} />
      <Route path="/after-school-activities" element={<AfterSchool />} />
      <Route path="/equestrian-training" element={<EquestrianTraining />} />
      <Route path="/music-schools" element={<MusicSchools />} />
      <Route path="/kids-gyms" element={<KidsGyms />} />
      <Route path="/play-centers" element={<PlayCenters />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/business-trial" element={<BusinessTrialSignup />} />
      <Route path="/advanced-signup" element={<AdvancedSignup />} />
      <Route path="/signup-cancelled" element={<SignupCancelled />} />
      <Route path="/checkout-success" element={<CheckoutSuccess />} />
      <Route path="/signup" element={<SignupFlow />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/pricing/:slug" element={<PricingPlan />} />
      <Route path="/features" element={<Features />} />
      <Route path="/hiveshare" element={<HiveShareLanding />} />
      {/* Redirect old URL for backward compatibility */}
      <Route path="/hivestream" element={<Navigate to="/hiveshare" replace />} />
      <Route path="/hiveshare-creator" element={<HiveShareCreator />} />
      <Route path="/hiveshare-live" element={<HiveShareLive />} />
      <Route path="/hiveshare/faq" element={<HiveShareFAQ />} />
      <Route path="/whats-new" element={<PublicWhatsNew />} />
      <Route path="/our-why" element={<OurWhy />} />
      <Route path="/search-demo" element={<SearchDemo />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/email-verification" element={<EmailVerification />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/account-suspended" element={<AccountSuspended />} />
      
      {/* Auth callback - should be accessible without auth */}
      <Route path="/callback" element={<PublicRoutes />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/set-password" element={<SetPassword />} />
      
      {/* Test route for debugging parent invitations */}
      <Route path="/test-invite" element={<TestInviteFunction />} />
      
      {/* Mobile auth debugging route */}
      <Route path="/mobile-auth-test" element={<MobileAuthTest />} />
      
      {/* Authenticated routes */}
      <Route path="/subscription-checkout" element={<SubscriptionCheckout />} />
      
      {/* Legacy routes - these will redirect to the proper admin routes */}
      <Route path="/classes" element={<AdminRoutes baseRoute="classes" />} />
      <Route path="/classes/new" element={<NewClassPage />} />
      <Route path="/classes/:id/edit" element={<EditClassPage />} />
      <Route path="/classes/new-event" element={<NewEventPage />} />
      <Route path="/classes/:id" element={<AdminRoutes baseRoute="classes" />} />
      
      {/* Auth-protected routes with AdminLayout */}
      <Route element={
        <AuthGate>
          <AdminRoleGuard>
            <SubscriptionGuard>
              <AdminLayout />
            </SubscriptionGuard>
          </AdminRoleGuard>
        </AuthGate>
      }>
        {/* Profile route */}
        <Route path="/profile" element={<Profile />} />
        
        {/* Dashboard route - Now using MaterialDashboard as primary */}
        <Route path="/dashboard" element={<Suspense fallback={<LazyLoadingSpinner />}><MaterialDashboard /></Suspense>} />
        
        {/* Legacy AI Dashboard preserved */}
        <Route path="/dashboard-future" element={<DashboardAI />} />
        
        {/* Messages routes */}
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:conversationId" element={<Messages />} />
        
        {/* Staff profile route */}
        <Route path="/staff/profile/:staffId" element={<StaffProfile />} />
        
        {/* Parent Portal Settings */}
        <Route path="/admin/parent-portal-settings" element={<ParentPortalSettings />} />
        
        {/* Direct Apparel route - ensure this works properly */}
        <Route path="/admin/apparel/*" element={<Apparel />} />
      </Route>
      
      {/* Check-in Kiosk route (fullscreen, no sidebar) */}
      <Route element={<AuthGate><AdminRoleGuard><Outlet /></AdminRoleGuard></AuthGate>}>
        <Route path="/check-in" element={<CheckIn />} />
      </Route>
      
      {/* Parent Portal Routes */}
      <Route path="/parent-portal/*" element={<ParentPortalRoutes />} />
      
      {/* Admin routes */}
      <Route path="/admin/*" element={<AdminRoutes />} />
      
      {/* SuperAdmin routes */}
      <Route path="/superadmin/*" element={<SuperAdminRoutes />} />
      
      {/* New public pages */}
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/eula" element={<Eula />} />
      
      {/* Catch all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
//comments
